import { Injectable } from '@angular/core';
import { ShopSettings } from '../../booking/models/shop-settings';
import { Observable, of } from '../../../../../node_modules/rxjs';
import { environment } from '../../../../environments/environment';
import { Gender } from '../../booking/models/gender';
import { PartnerTitle } from '../../partner/models/partner-title';
import { MaintenanceWebshopModeType } from '../models/maintenance-webshop-mode-type';
import { ShopTypeEnum } from './shop-type.enum';
// import { APP_BASE_HREF } from '@angular/common';

@Injectable({
  providedIn: 'root'
})
export class ShopSettingsService {

  protected _shopSettings: ShopSettings = null;

  // Webshop settings
  protected _defaultStartSellTicketsFromDate: Date = null; // no restriction
  protected _defaultSingleTicketsShowForDays = -1; // -1 no restrictions
  protected _defaultEventTicketsShowForDays = -1; // -1 no restrictions
  protected _defaultAllowTodayBookings = true; // what default ?
  protected _sessionTimeOut = 0;
  protected _sessionTimeOutLoggedIn = 0;
  protected _sessionTimeOutCountdown = 60;
  protected _defaultFamilyChildrenName = 'Kinder, Gyermek';
  protected _defaultFamilyParentName = 'Erwachsene, Felnőtt';
  protected _defaultFamilyParentsLimit = 0;
  protected _defaultFamilyChildrenLimit = 0;
  protected _defaultGenderList = new Array<Gender>(new Gender('F', 'Female'), new Gender('M', 'Male'));
  protected _defaultIsMaintenanceWebshop = false;
  protected _defaultMaintenanceWebshopText = '';
  protected _localStorageKeyisVUMode = 'vu_mode';
  protected _localStorageKeyProfile = 'profile';
  protected _defaultPartnerTitleList = new Array<PartnerTitle>();
  protected _defaultQuantityOfAttemptsToCheckPaymentCompletion = 10;

  shopType: ShopTypeEnum;

  constructor() { }
  // constructor(@Inject(APP_BASE_HREF) private baseHref: string) {}

  initialize(): Observable<boolean> {
    return of();
  }

  get shopSettings(): ShopSettings {
    return null;
  }

  getMinDate(isEvent: boolean): Date {
    const minAllowed = new Date();
    if ((!this.allowTodayBookings && !isEvent) || isEvent) {
      minAllowed.setDate(minAllowed.getDate() + 1);
    }
    const res = this.restrictDate && this.restrictDate > minAllowed ? this.restrictDate : minAllowed;
    res.setHours(0, 0, 0, 0);
    return res;
  }

  getMaxDate(isEvent: boolean): Date {
    const restrictLength = isEvent ? this.restrictEventLength : this.restrictSingleLength;
    if (restrictLength > 0) {
      const maxDate = new Date(this.getMinDate(isEvent));
      maxDate.setDate(maxDate.getDate() + restrictLength);
      maxDate.setHours(0, 0, 0, 0);
      return maxDate;
    }
    return null;
  }

  // Start Sell Tickets From Date
  get restrictDate(): Date {
    return this._shopSettings ? this._shopSettings.startSellTicketsFromDate : this._defaultStartSellTicketsFromDate;
  }

  // Show single tickets for days
  get restrictSingleLength(): number {
    return this._shopSettings ? this._shopSettings.singleTicketsShowForDays : this._defaultSingleTicketsShowForDays;
  }

  // Show event tickets for days
  get restrictEventLength(): number {
    return this._shopSettings ? this._shopSettings.eventTicketsShowForDays : this._defaultEventTicketsShowForDays;
  }

  // Allow today bookings
  get allowTodayBookings(): boolean {
    return this._shopSettings ? this._shopSettings.allowTodayBookings : this._defaultAllowTodayBookings;
  }

  // Session Time Out in minutes
  get sessionTimeOut(): number {
    return this._shopSettings ? this._shopSettings.sessionTimeOut : this._sessionTimeOut;
  }

  get sessionTimeOutLoggedIn(): number {
    return this._shopSettings ? this._shopSettings.sessionTimeOutLoggedIn : this._sessionTimeOutLoggedIn;
  }

  get sessionTimeOutCountdown(): number {
    return this._shopSettings ? this._shopSettings.sessionTimeOutCountdown : this._sessionTimeOutCountdown;
  }

  get googleAnalyticsKey(): string {
    return this._shopSettings && this._shopSettings.googleAnalyticsKey ? this._shopSettings.googleAnalyticsKey : null;
  }

  get gtagGlobalManagerKey(): string {
    return this._shopSettings && this._shopSettings.gtagGlobalManagerKey ? this._shopSettings.gtagGlobalManagerKey : null;
  }

  get gtagLocalManagerKey(): string {
    return this._shopSettings && this._shopSettings.gtagLocalManagerKey ? this._shopSettings.gtagLocalManagerKey : null;
  }

  get facebookAnalyticsId(): string {
    return this._shopSettings && this._shopSettings.facebookAnalyticsId ? this._shopSettings.facebookAnalyticsId : null;
  }

  get minimalAgeToSignWaiver(): number {
    return this._shopSettings && this._shopSettings.minimalAgeToSignWaiver ? this._shopSettings.minimalAgeToSignWaiver : environment.globalSettings.minAgeForRegistration;
  }

  get familyParentName(): string {
    return this._shopSettings && this._shopSettings.familyParentName ? this._shopSettings.familyParentName : this._defaultFamilyParentName;
  }

  get familyChildrenName(): string {
    return this._shopSettings && this._shopSettings.familyChildrenName ? this._shopSettings.familyChildrenName : this._defaultFamilyChildrenName;
  }

  get familyParentsLimit(): number {
    return this._shopSettings && this._shopSettings.familyParentsLimit ? this._shopSettings.familyParentsLimit : this._defaultFamilyParentsLimit;
  }

  get familyChildrenLimit(): number {
    return this._shopSettings && this._shopSettings.familyChildrenLimit ? this._shopSettings.familyChildrenLimit : this._defaultFamilyChildrenLimit;
  }

  get genderList(): Array<Gender> {
    return this._shopSettings && this._shopSettings.genderList && this._shopSettings.genderList.length ? this._shopSettings.genderList : this._defaultGenderList;
  }

  get partnerTitleList(): Array<PartnerTitle> {
    return this._shopSettings && this._shopSettings.partnerTitleList && this._shopSettings.partnerTitleList.length ? this._shopSettings.partnerTitleList : this._defaultPartnerTitleList;
  }

  get isMaintenanceWebshop(): boolean {
    if (!this._shopSettings || !this._shopSettings.maintenanceWebshopMode) {
      return this._defaultIsMaintenanceWebshop;
    }

    const maintenanceWebshopMode = MaintenanceWebshopModeType[this._shopSettings.maintenanceWebshopMode];
    switch (maintenanceWebshopMode) {
      case MaintenanceWebshopModeType.All:
        return true;
      case MaintenanceWebshopModeType.Website:
        return !this.isVUMode;
      case MaintenanceWebshopModeType.VU:
        return this.isVUMode;
    }

    return false;
  }

  get maintenanceWebshopText(): string {
    return this._shopSettings ? this._shopSettings.maintenanceWebshopText : this._defaultMaintenanceWebshopText;
  }

  get isVUMode(): boolean {
    return JSON.parse(localStorage.getItem(this._localStorageKeyisVUMode)) || false;
  }

  set isVUMode(isVUMode: boolean) {
    localStorage.setItem(this._localStorageKeyisVUMode, JSON.stringify(isVUMode));
  }

  get easyRegistration(): boolean {
    return true;
  }

  get logoUrl(): string {
    return this.shopSettings.logoUrl;
  }

  get allowedRegistration(): boolean {
    return !this.shopSettings.partnerCodeAuthorization || this.isVUMode;
  }

  //  get siteBasePath(): string {
  //    return this.baseHref;
  //  }

  get hideDateEndInOrderDetail(): boolean {
    return this.shopSettings && this.shopSettings.hideDateEndInOrderDetail ? this.shopSettings.hideDateEndInOrderDetail : false;
  }

  get isPosOrderMode(): boolean {
    return this.isVUMode && this.shopType === ShopTypeEnum.PosOrderWebshop;
  }

  get isWebshopMode(): boolean {
    if (!environment.shopMode || ['webshop', 'webshop&kiosk'].indexOf(environment.shopMode) !== -1) {
      return true;
    }
    return false;
  }

  get profile(): string {
    return localStorage.getItem(this._localStorageKeyProfile) || '';
  }

  set profile(profile: string) {
    if (!profile) {
      localStorage.removeItem(this._localStorageKeyProfile);
    } else {
      localStorage.setItem(this._localStorageKeyProfile, profile);
    }
  }

  get vuBaseUrl(): string {
    return this.shopSettings.vuBaseUrl || environment.vuUrl;
  }

  get showFullNameInRegistration(): boolean {
    return this._shopSettings ? this._shopSettings.showFullNameInRegistration : false;
  }

  get showAddressInRegistration(): boolean {
    return this._shopSettings ? this._shopSettings.showAddressInRegistration : false;
  }

  get showPhoneInRegistration(): boolean {
    return this._shopSettings ? this._shopSettings.showPhoneInRegistration : false;
  }

  get quantityOfAttemptsToCheckPaymentCompletion(): number {
    return this._shopSettings?.quantityOfAttemptsToCheckPaymentCompletion
      || this._defaultQuantityOfAttemptsToCheckPaymentCompletion;
  }
}
